import { useState, useCallback, useRef, useEffect } from 'react'
import { NO_USER } from '../../Api/firebase/AuthData'
import type { User, UserFavorites } from '../../Api/Users/types'
import type { FavType } from '../../Api/firebase/AuthData'
import type { AuthState } from './types'

const defaultFavs: UserFavorites = {
  topics: [],
  favPlayers: [],
  favTeam: null,
}

export const useAuthState = () => {
  const isMountedRef = useRef(true)
  const [user, setUser] = useState<User | null>(null)
  const [authLoading, setAuthLoading] = useState<boolean>(true)
  const [userFavorites, setUserFavorites] = useState<UserFavorites>(defaultFavs)
  const [showLogin, setShowLogin] = useState<{
    show: boolean
    extraFields: string[]
  }>({ show: false, extraFields: [] })

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const safeSetUser = useCallback((newUser: User | null) => {
    if (isMountedRef.current) {
      setUser(newUser)
    }
  }, [])

  const safeSetAuthLoading = useCallback((loading: boolean) => {
    if (isMountedRef.current) {
      setAuthLoading(loading)
    }
  }, [])

  const safeSetUserFavorites = useCallback((favorites: UserFavorites) => {
    if (isMountedRef.current) {
      setUserFavorites(favorites)
    }
  }, [])

  const safeSetShowLogin = useCallback(
    (show: { show: boolean; extraFields: string[] }) => {
      if (isMountedRef.current) {
        setShowLogin(show)
      }
    },
    []
  )

  const updateFavsInState = useCallback(
    (favType: FavType, favorites: string[], favTeam?: string) => {
      if (!isMountedRef.current) return

      setUserFavorites((prevFavs) => {
        const updatedFavs: UserFavorites = { ...prevFavs }

        if (favType === 'favPlayers') {
          updatedFavs.favPlayers = favorites
        }
        if (favType === 'topics') {
          updatedFavs.topics = favorites
        }
        if (favType === 'favTeam' && favTeam) {
          updatedFavs.favTeam = favTeam
        }

        return updatedFavs
      })
    },
    []
  )

  const updateProfileInState = useCallback((userItem: Partial<User>) => {
    if (!isMountedRef.current) return

    setUser((currentUser) => {
      if (currentUser === null) return null

      const updatedUser = { ...currentUser, ...userItem }

      // Update favorites if favTeam is included
      if (userItem.favTeam) {
        setUserFavorites((currentFavs) => ({
          ...currentFavs,
          favTeam: userItem.favTeam!,
        }))
      }

      return updatedUser
    })
  }, [])

  const updateUserFieldInState = useCallback(
    <K extends keyof User>(fieldName: K, value: User[K]) => {
      if (!isMountedRef.current) return

      setUser((currentUser) => {
        if (currentUser === null) return null
        return { ...currentUser, [fieldName]: value }
      })
    },
    []
  )

  const clearTeamFavorite = useCallback(() => {
    if (!isMountedRef.current) return

    setUserFavorites((prevFavs) => ({
      ...prevFavs,
      favTeam: null,
    }))
  }, [])

  const resetAuthState = useCallback(() => {
    if (!isMountedRef.current) return
    setUser(NO_USER)
    setUserFavorites(defaultFavs)
    setShowLogin({ show: false, extraFields: [] })
    setAuthLoading(false)
  }, [])

  const login = useCallback((extraFields?: string[]) => {
    if (!isMountedRef.current) return

    setShowLogin({
      show: true,
      extraFields: extraFields || [],
    })
  }, [])

  const handleToggleLogin = useCallback(() => {
    if (!isMountedRef.current) return
    setShowLogin((prev) => ({
      show: !prev.show,
      extraFields: [],
    }))
  }, [])

  const getMissingUserFields = useCallback(
    (fields: string[]): string[] => {
      if (!user || !fields.length) return []
      return fields.filter((field) => !user[field as keyof User])
    },
    [user]
  )

  const authState: AuthState = {
    user,
    userFavorites,
    authLoading,
    showLogin,
  }

  return {
    ...authState,
    setUser: safeSetUser,
    setAuthLoading: safeSetAuthLoading,
    setUserFavorites: safeSetUserFavorites,
    setShowLogin: safeSetShowLogin,
    updateFavsInState,
    updateProfileInState,
    updateUserFieldInState,
    clearTeamFavorite,
    resetAuthState,
    login,
    handleToggleLogin,
    getMissingUserFields,
    isMountedRef,
  }
}
